import React from 'react';
import { DEFAULT_COMPOSED_CHART_Y_WIDTH_VERTICAL } from '@components/Widgets/utils';
import { FeatureFlags } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { emptyArray, emptyObject } from '@fabric-ui/utils';
import { isBoolean } from '@utils/utils';
import { useAvContext } from '../../../context/AvContextProvider';
import { formatTooltipNumberValue } from '../../../views/CustomDashboards/components/Widgets/tooltipNumberFormatter';
import { getCustomTickFormatter } from '../../../views/CustomDashboards/hooks';
import { AxisTickType, ChartPosition, LabelFormatterOption } from '../../../views/CustomDashboards/types/types';
import { AvComposedWidget, AvComposedWidgetProps } from '../AvComposedWidget';
import { formatValue } from '../AvComposedWidget/AvComposedWidget.utils';
import { ChartClickInteraction } from '../types';
import { WidgetTooltipContent, type WidgetTooltipContentProps } from '../WidgetTooltip/WidgetTooltip';

interface Props {
  data?: any[];
  series?: any[];
  dateFormat?: string;
  sx?: AvComposedWidgetProps['sx'];
  className?: string;
  isDate: boolean;
  xAxisKey?: string;
  customLabels?: { x?: string; y?: string };
  labelFormatter?: LabelFormatterOption;
  lineChartPosition?: ChartPosition;
  showDataLabels?: boolean;
  measurements?: ReturnType<typeof useAvContext>['measurements'];
  calculatedDimensions?: ReturnType<typeof useAvContext>['calculatedDimensions'];
  featureFlags?: ReturnType<typeof useAvContext>['featureFlags'];
  clickInteractions?: ChartClickInteraction[];
  selected?: { name: string }[];
  onSelect?: (v: any) => void;
}

export default function LineWidgetBasis({
  data = emptyArray,
  series = emptyArray,
  dateFormat = 'MMM',
  sx,
  className,
  isDate,
  xAxisKey = 'date',
  customLabels = emptyObject,
  labelFormatter,
  lineChartPosition,
  showDataLabels,
  featureFlags = emptyObject,
  measurements = emptyObject,
  calculatedDimensions = emptyObject,
  onSelect,
  clickInteractions,
  selected,
}: Props) {
  const dataKeyMap = series.reduce((map, { name, dataKey }) => ({ ...map, [dataKey]: name }), {});

  const topDataPlusOthers = data.map(({ [xAxisKey]: name, originalValue, ...values }) => ({
    [xAxisKey]: isBoolean(name) ? name.toString() : name,
    originalValue,
    ...Object.keys(values).reduce(
      (obj, key) => ({ ...obj, ...(dataKeyMap[key] ? { [key]: values[key] } : { OTHERS: (obj.OTHERS || 0) + values[key] }) }),
      {
        OTHERS: 0,
      }
    ),
  }));

  const xAxisTickType =
    labelFormatter !== LabelFormatterOption.SOURCE_ICONS && data?.length > 6 ? AxisTickType.Slanted : AxisTickType.Horizontal;

  const customTickFormatter = getCustomTickFormatter({
    labelFormattingOption: labelFormatter,
    rotation: xAxisTickType,
    sx: { mt: 3, ...flex.center },
  });

  const defaultAxisProps = {
    x: { customTickFormatter } as NonNullable<NonNullable<AvComposedWidgetProps['defaultAxisProps']>['x']>,
    y: { width: lineChartPosition?.margin?.left || DEFAULT_COMPOSED_CHART_Y_WIDTH_VERTICAL } as NonNullable<
      NonNullable<AvComposedWidgetProps['defaultAxisProps']>['y']
    >,
    xLabel: {},
  } as const satisfies AvComposedWidgetProps['defaultAxisProps'];

  return (
    <AvComposedWidget
      showDataLabels={showDataLabels}
      xAxisKey={xAxisKey}
      isDate={isDate}
      series={series}
      data={topDataPlusOthers}
      dateFormat={dateFormat}
      sx={sx}
      className={className}
      labels={customLabels}
      chartProps={{ xAxisTickType }}
      defaultAxisProps={defaultAxisProps}
      onSelect={onSelect}
      clickInteractions={clickInteractions}
      selected={selected}
      enableLockTooltip={!selected?.length}
      getTooltipContent={
        featureFlags[FeatureFlags.NewChartTooltip]
          ? // eslint-disable-next-line react/no-unstable-nested-components
            props => (
              <WidgetTooltipContent
                {...props}
                widgetData={topDataPlusOthers}
                interactions={clickInteractions}
                {...getLineWidgetTooltipContentProps({
                  payload: props.payload,
                  measurements: { ...measurements.asObject, ...calculatedDimensions.asObject },
                  xAxisKey,
                  isDate,
                  dateFormat,
                })}
              />
            )
          : undefined
      }
    />
  );
}

const getLineWidgetTooltipContentProps = (
  props: Pick<WidgetTooltipContentProps, 'payload'> & {
    measurements: Record<string, any>;
    xAxisKey?: string;
    isDate?: boolean;
    dateFormat?: string;
  }
) => {
  const { payload, measurements, xAxisKey, isDate, dateFormat } = props;

  const content: WidgetTooltipContentProps['content'] = payload?.map(({ name, color, value, dataKey }) => ({
    title: name,
    dotColor: color,
    value: formatTooltipNumberValue(value),
    description: dataKey ? measurements[dataKey]?.description : undefined,
  }));

  const dimensionNames = xAxisKey ? payload?.at(0)?.payload?.[xAxisKey] : [];
  const title = (Array.isArray(dimensionNames) ? dimensionNames : [dimensionNames])
    .map(name => formatValue(isDate, dateFormat, name))
    .join(', ');

  return {
    content,
    title,
  };
};
